import { TerraDailyData, TerraSleepData } from '@interfaces/terra.interface';
import { Group, Title, Spoiler, Text } from '@mantine/core';
import { formatSecondsToHoursMinutesSeconds } from '@utils/date';

interface TerraDailyDataRecapProps {
  data: TerraDailyData;
  sleepData: TerraSleepData | null;
}

export default function TerraDailyDataRecap({
  data,
  sleepData,
}: TerraDailyDataRecapProps) {
  return (
    <Group gap="xl" align="start">
      <div>
        <Title order={4} mb="md">
          Frequenza cardiaca
        </Title>
        <Text>
          <Text fw={700} span>
            Minima:
          </Text>{' '}
          {data.heartRateDaily.min_hr_bpm
            ? `${data.heartRateDaily.min_hr_bpm.toFixed(0)} bpm`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Massima:
          </Text>{' '}
          {data.heartRateDaily.max_hr_bpm
            ? `${data.heartRateDaily.max_hr_bpm.toFixed(0)} bpm`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Media:
          </Text>{' '}
          {data.heartRateDaily.avg_hr_bpm
            ? `${data.heartRateDaily.avg_hr_bpm.toFixed(0)} bpm`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Media rmssd:
          </Text>{' '}
          {data.heartRateDaily.avg_hrv_rmssd
            ? `${data.heartRateDaily.avg_hrv_rmssd} bpm`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Media sdnn:
          </Text>{' '}
          {data.heartRateDaily.avg_hrv_sdnn
            ? `${data.heartRateDaily.avg_hrv_sdnn} bpm`
            : 'N/A'}
        </Text>
      </div>
      <div>
        <Title order={4} mb="md">
          Ossigenazione sangue
        </Title>
        <Text>
          <Text fw={700} span>
            Media:
          </Text>{' '}
          {`${data.oxygenSaturationDaily.avg_saturation_percentage ? `${data.oxygenSaturationDaily.avg_saturation_percentage.toFixed(0)}%` : 'N/A'}`}
        </Text>
      </div>
      <div>
        <Title order={4} mb="md">
          Calorie
        </Title>
        <Text>
          <Text fw={700} span>
            Assunte:
          </Text>{' '}
          {data.caloriesDaily.net_intake_calories
            ? `${data.caloriesDaily.net_intake_calories.toFixed(0)} kcal`
            : 'N/A'}
        </Text>
      </div>
      <div>
        <Title order={4} mb="md">
          Stress
        </Title>
        <Text>
          <Text fw={700} span>
            Media:
          </Text>{' '}
          {data.stressDaily.avg_stress_level
            ? `${data.stressDaily.avg_stress_level.toFixed(0)}`
            : 'N/A'}
        </Text>
      </div>
      <div>
        <Title order={4} mb="md">
          Attività
        </Title>
        <Text>
          <Text fw={700} span>
            Inattività:
          </Text>{' '}
          {data.activeDurationsDaily.inactivity_seconds
            ? `${formatSecondsToHoursMinutesSeconds(data.activeDurationsDaily.inactivity_seconds)}`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Attività bassa:
          </Text>{' '}
          {data.activeDurationsDaily.low_intensity_seconds
            ? `${formatSecondsToHoursMinutesSeconds(data.activeDurationsDaily.low_intensity_seconds)}`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Attività moderata:
          </Text>{' '}
          {data.activeDurationsDaily.moderate_intensity_seconds
            ? `${formatSecondsToHoursMinutesSeconds(data.activeDurationsDaily.moderate_intensity_seconds)}`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Attività vigorosa:
          </Text>{' '}
          {data.activeDurationsDaily.vigorous_intensity_seconds
            ? `${formatSecondsToHoursMinutesSeconds(data.activeDurationsDaily.vigorous_intensity_seconds)}`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Attività totale:
          </Text>{' '}
          {data.activeDurationsDaily.activity_seconds
            ? `${formatSecondsToHoursMinutesSeconds(data.activeDurationsDaily.activity_seconds)}`
            : 'N/A'}
        </Text>
      </div>
      <div>
        <Title order={4} mb="md">
          Distanza
        </Title>
        <Text>
          <Text fw={700} span>
            Passi:
          </Text>{' '}
          {data.distanceDaily.steps
            ? `${data.distanceDaily.steps.toFixed(0)}`
            : 'N/A'}
        </Text>
        <Text>
          <Text fw={700} span>
            Distanza:
          </Text>{' '}
          {data.distanceDaily.distance_meters
            ? `${data.distanceDaily.distance_meters.toFixed(0)} m`
            : 'N/A'}
        </Text>
      </div>
      {sleepData && (
        <div>
          <Title order={4} mb="md">
            Sonno
          </Title>
          <Spoiler
            maxHeight={150}
            showLabel="Mostra di più"
            hideLabel="Mostra meno"
          >
            <Text>
              <Text fw={700} span>
                Efficienza:
              </Text>{' '}
              {sleepData.sleepDurationDaily.sleep_efficiency
                ? `${(sleepData.sleepDurationDaily.sleep_efficiency * 100).toFixed(0)}%`
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Durata del sonno:
              </Text>{' '}
              {sleepData.sleepDurationDaily.asleep.duration_asleep_state_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.asleep
                      .duration_asleep_state_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Sonno REM:
              </Text>{' '}
              {sleepData.sleepDurationDaily.asleep
                .duration_REM_sleep_state_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.asleep
                      .duration_REM_sleep_state_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Sonno profondo:
              </Text>{' '}
              {sleepData.sleepDurationDaily.asleep
                .duration_deep_sleep_state_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.asleep
                      .duration_deep_sleep_state_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Sonno leggero:
              </Text>{' '}
              {sleepData.sleepDurationDaily.asleep
                .duration_light_sleep_state_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.asleep
                      .duration_light_sleep_state_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Durata sveglio:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake.duration_awake_state_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake
                      .duration_awake_state_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Respiri per minuto:
              </Text>{' '}
              {sleepData.respirationDaily.breaths_per_minute
                ? `${sleepData.respirationDaily.breaths_per_minute.toFixed(0)}`
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Eventi REM:
              </Text>{' '}
              {sleepData.sleepDurationDaily.asleep.num_REM_events
                ? `${sleepData.sleepDurationDaily.asleep.num_REM_events}`
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Durata totale sveglio:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake.total_duration_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake.total_duration_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Eventi di risveglio:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake.num_wakeup_events
                ? `${sleepData.sleepDurationDaily.awake.num_wakeup_events}`
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Latenza del sonno:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake.sleep_latency_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake.sleep_latency_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Latenza al risveglio:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake.wake_up_latency_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake.wake_up_latency_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Interruzioni brevi:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake
                .duration_short_interruption_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake
                      .duration_short_interruption_seconds,
                  )
                : 'N/A'}
            </Text>
            <Text>
              <Text fw={700} span>
                Interruzioni lunghe:
              </Text>{' '}
              {sleepData.sleepDurationDaily.awake
                .duration_long_interruption_seconds
                ? formatSecondsToHoursMinutesSeconds(
                    sleepData.sleepDurationDaily.awake
                      .duration_long_interruption_seconds,
                  )
                : 'N/A'}
            </Text>
          </Spoiler>
        </div>
      )}
    </Group>
  );
}
